<template>
  <modal
    :show="visible"
    :title="dialogHeader"
    size="large"
    :closable="!isCalculating"
    elem-id="modal_payroll-calculations"
    data-testid="payroll-calculations-dialog"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Progress View (while calculating) -->
    <CalculationsProgressView
      v-if="isCalculating || (!isComplete && steps.length > 0)"
      :steps="steps"
    />

    <!-- Results View (after complete) -->
    <CalculationsResultsView
      v-else-if="isComplete"
      :results="results"
      :user-breakdown="userBreakdown"
      @close="handleClose"
    />

    <!-- Empty State (no rules to calculate) -->
    <div v-else class="empty-state">
      <i class="pi pi-info-circle" />
      <p>{{ t('Nenhuma regra de pagamento encontrada para calcular') }}</p>
      <Button
        :label="t('Fechar')"
        icon="pi pi-times"
        class="p-button-secondary"
        data-testid="payroll-calculations-close-button"
        @click="handleClose"
      />
    </div>

    <!-- Footer (only during progress, before complete) -->
    <template v-if="!isCalculating && !isComplete && steps.length > 0" #footer>
      <div class="dialog-footer">
        <Button
          :label="t('Cancelar')"
          icon="pi pi-times"
          class="p-button-secondary"
          data-testid="payroll-calculations-cancel-button"
          @click="handleClose"
        />
        <Button
          :label="t('Iniciar Calculos')"
          icon="pi pi-play"
          :disabled="isCalculating"
          data-testid="payroll-calculations-start-button"
          @click="runCalculations"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CalculationsProgressView from './CalculationsProgressView.vue';
import CalculationsResultsView from './CalculationsResultsView.vue';
import { usePayrollCalculations } from '@/composables/usePayrollCalculations';
import { useExpenseConciliation } from '@/composables/useExpenseConciliation';
import { PERIOD_STATUS, getPeriodStatus, updatePeriodStatus } from '@/composables/usePayrollPeriods';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;
type StepStatus = 'pending' | 'running' | 'completed' | 'error';

interface PaymentRule {
  rule_type: string;
  [key: string]: unknown;
}

interface CalculationStep {
  key: string;
  title: string;
  loadingText: string;
  status: StepStatus;
  result: number | null;
  errorMessage: string | null;
}

interface CalculationResults {
  executed: number | null;
  soldRemotely: number | null;
  fixed: number | null;
}

interface UserBreakdownItem {
  user_name?: string;
  gross_total?: number | string;
  [key: string]: unknown;
}

interface CalculationApiResult {
  totals?: { net_amount?: number };
  by_user?: UserBreakdownItem[];
  [key: string]: unknown;
}

const { t } = useI18n();

const props = defineProps<{
  visible: boolean;
  periodId: Id;
  franchiseId: Id;
  rules?: PaymentRule[];
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'calculations-complete': [payload: {
    grandTotal: number;
    executedTotal: number;
    soldRemotelyTotal: number;
    fixedTotal: number;
    userBreakdown: UserBreakdownItem[];
  }];
  closed: [];
}>();

// Composables
const {
  calculateCommissionOverGrossExecuted,
  calculateCommissionOverGrossSoldRemotely,
  calculateFixed,
  markPeriodCalculated,
  getPeriodCalculationSummary
} = usePayrollCalculations();

const { completeExpenseConciliation } = useExpenseConciliation();

// =============================================================================
// STATE
// =============================================================================

const isCalculating = ref(false);
const isComplete = ref(false);
const steps = ref<CalculationStep[]>([]);
const results = ref<CalculationResults>({
  executed: null,
  soldRemotely: null,
  fixed: null
});
const userBreakdown = ref<UserBreakdownItem[]>([]);

// =============================================================================
// COMPUTED
// =============================================================================

const dialogHeader = computed((): string => {
  if (isCalculating.value) return t('Processando Calculos...');
  if (isComplete.value) return t('Calculos Concluidos');
  return t('Calcular Folha de Pagamento');
});

const hasExecutedRules = computed((): boolean =>
  (props.rules ?? []).some(r => r.rule_type === 'commission_over_gross_revenue_executed')
);

const hasSoldRemotelyRules = computed((): boolean =>
  (props.rules ?? []).some(r => r.rule_type === 'commission_over_gross_revenue_sold_remotely')
);

const hasFixedRules = computed((): boolean =>
  (props.rules ?? []).some(r => r.rule_type === 'fixed')
);

// =============================================================================
// STEP CONFIGURATION
// =============================================================================

const buildSteps = (): void => {
  const stepList: CalculationStep[] = [];

  if (hasExecutedRules.value) {
    stepList.push({
      key: 'executed',
      title: t('Comissão Executado'),
      loadingText: t('Calculando comissao sobre receita executada...'),
      status: 'pending',
      result: null,
      errorMessage: null
    });
  }

  if (hasSoldRemotelyRules.value) {
    stepList.push({
      key: 'sold_remotely',
      title: t('Comissão Vendido'),
      loadingText: t('Calculando comissao sobre receita vendida...'),
      status: 'pending',
      result: null,
      errorMessage: null
    });
  }

  if (hasFixedRules.value) {
    stepList.push({
      key: 'fixed',
      title: t('Valores Fixos'),
      loadingText: t('Registrando valores fixos...'),
      status: 'pending',
      result: null,
      errorMessage: null
    });
  }

  steps.value = stepList;
};

// =============================================================================
// CALCULATION LOGIC
// =============================================================================

const updateStepStatus = (key: string, status: StepStatus, result: number | null = null, errorMessage: string | null = null): void => {
  const step = steps.value.find(s => s.key === key);
  if (step) {
    step.status = status;
    if (result !== null) step.result = result;
    if (errorMessage) step.errorMessage = errorMessage;
  }
};

const runStep = async (stepKey: string): Promise<boolean> => {
  updateStepStatus(stepKey, 'running');

  try {
    let apiResult: CalculationApiResult | undefined;

    switch (stepKey) {
      case 'executed':
        // TODO(contract-debt): Payroll_CalculateCommissionOverGrossExecutedResponse.totals
        // is shape-erased to Record<string, unknown> (see @shape-erased-exception in PHP);
        // type a Row for the totals payload (net_amount, base_amount, etc.) to drop this cast.
        apiResult = await calculateCommissionOverGrossExecuted(props.periodId, props.franchiseId) as unknown as CalculationApiResult;
        results.value.executed = apiResult?.totals?.net_amount ?? 0;
        break;

      case 'sold_remotely':
        // TODO(contract-debt): Payroll_CalculateCommissionOverGrossSoldRemotelyResponse.totals
        // is shape-erased to Record<string, unknown> (see @shape-erased-exception in PHP);
        // type a Row for the totals payload (net_amount, base_amount, etc.) to drop this cast.
        apiResult = await calculateCommissionOverGrossSoldRemotely(props.periodId, props.franchiseId) as unknown as CalculationApiResult;
        results.value.soldRemotely = apiResult?.totals?.net_amount ?? 0;
        break;

      case 'fixed':
        // TODO(contract-debt): Payroll_CalculateFixedResponse.totals is shape-erased to
        // Record<string, unknown> (see @shape-erased-exception in PHP); type a Row for
        // the totals payload (net_amount, base_amount, etc.) to drop this cast.
        apiResult = await calculateFixed(props.periodId, props.franchiseId) as unknown as CalculationApiResult;
        results.value.fixed = apiResult?.totals?.net_amount ?? 0;
        break;
    }

    const stepResult = stepKey === 'sold_remotely' ? results.value.soldRemotely : results.value[stepKey as keyof CalculationResults];
    updateStepStatus(stepKey, 'completed', stepResult);

    log('PayrollCalculationsDialog', 'runStep', 'Step completed', {
      stepKey,
      result: apiResult?.totals?.net_amount
    });

    return true;
  } catch (err) {
    const error = err as Error;
    log('PayrollCalculationsDialog', 'runStep', 'Step failed', {
      stepKey,
      error: error.message
    });

    updateStepStatus(stepKey, 'error', null, error.message);
    return false;
  }
};

const ensurePeriodStatusForCalculations = async (): Promise<void> => {
  const currentStatus = getPeriodStatus(Number(props.periodId));

  log('PayrollCalculationsDialog', 'ensurePeriodStatusForCalculations', 'Checking status', {
    periodId: props.periodId,
    currentStatus,
    requiredMinStatus: PERIOD_STATUS.EXPENSES
  });

  if (currentStatus !== undefined && currentStatus < PERIOD_STATUS.EXPENSES) {
    log('PayrollCalculationsDialog', 'ensurePeriodStatusForCalculations', 'Period below EXPENSES, auto-completing', {
      currentStatus
    });

    try {
      await completeExpenseConciliation(Number(props.periodId));
      updatePeriodStatus(Number(props.periodId), PERIOD_STATUS.EXPENSES, 'PayrollCalculationsDialog_autoComplete');
      log('PayrollCalculationsDialog', 'ensurePeriodStatusForCalculations', 'Expenses auto-completed');
    } catch (err) {
      const error = err as Error;
      log('PayrollCalculationsDialog', 'ensurePeriodStatusForCalculations', 'Failed to auto-complete expenses', {
        error: error.message
      });
      // Continue anyway - markPeriodCalculated will give clearer error
    }
  }
};

const runCalculations = async (): Promise<void> => {
  isCalculating.value = true;
  isComplete.value = false;

  log('PayrollCalculationsDialog', 'runCalculations', 'Starting calculations', {
    periodId: props.periodId,
    franchiseId: props.franchiseId,
    stepsCount: steps.value.length
  });

  try {
    await ensurePeriodStatusForCalculations();

    for (const step of steps.value) {
      const success = await runStep(step.key);
      if (!success) {
        log('PayrollCalculationsDialog', 'runCalculations', 'Step failed, continuing with next', {
          stepKey: step.key
        });
      }
    }

    try {
      await markPeriodCalculated(props.periodId, props.franchiseId);
      log('PayrollCalculationsDialog', 'runCalculations', 'Period marked as calculated');
    } catch (err) {
      const error = err as Error;
      log('PayrollCalculationsDialog', 'runCalculations', 'Failed to mark period calculated', {
        error: error.message
      });
      eventBus.emit('globalMessage.error', {
        message: t('Erro ao marcar periodo como calculado'),
        duration: 5000
      });
    }

    try {
      // TODO(contract-debt): Payroll_PeriodCalculationSummaryResponse.summary is
      // shape-erased to `object` (see @shape-erased-exception in PHP); by_user lives
      // inside that aggregate. Author a typed Row for the summary payload to drop this cast.
      const summary = await getPeriodCalculationSummary(props.periodId, Number(props.franchiseId)) as unknown as CalculationApiResult;
      userBreakdown.value = summary?.by_user ?? [];
    } catch (err) {
      const error = err as Error;
      log('PayrollCalculationsDialog', 'runCalculations', 'Failed to load user breakdown', {
        error: error.message
      });
    }

    isComplete.value = true;

    log('PayrollCalculationsDialog', 'runCalculations', 'All calculations complete', {
      results: results.value
    });

  } catch (err) {
    const error = err as Error;
    log('PayrollCalculationsDialog', 'runCalculations', 'Calculations failed', {
      error: error.message
    });

    eventBus.emit('globalMessage.error', {
      message: error.message || t('Erro ao processar calculos'),
      duration: 5000
    });
  } finally {
    isCalculating.value = false;
  }
};

// =============================================================================
// HANDLERS
// =============================================================================

const handleClose = (): void => {
  if (isComplete.value) {
    const grandTotal =
      (results.value.executed ?? 0) +
      (results.value.soldRemotely ?? 0) +
      (results.value.fixed ?? 0);

    emit('calculations-complete', {
      grandTotal,
      executedTotal: results.value.executed ?? 0,
      soldRemotelyTotal: results.value.soldRemotely ?? 0,
      fixedTotal: results.value.fixed ?? 0,
      userBreakdown: userBreakdown.value
    });
  }

  emit('update:visible', false);
  emit('closed');
};

const resetState = (): void => {
  isCalculating.value = false;
  isComplete.value = false;
  steps.value = [];
  results.value = {
    executed: null,
    soldRemotely: null,
    fixed: null
  };
  userBreakdown.value = [];
};

// =============================================================================
// WATCHERS
// =============================================================================

watch(() => props.visible, (newVisible: boolean) => {
  if (newVisible) {
    resetState();
    buildSteps();

    log('PayrollCalculationsDialog', 'watch:visible', 'Dialog opened, steps built', {
      stepsCount: steps.value.length,
      stepKeys: steps.value.map(s => s.key)
    });
  }
});

watch(() => props.rules, () => {
  if (props.visible && !isCalculating.value) {
    buildSteps();
  }
}, { deep: true });
</script>

<style scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: var(--surface-400);
}

.empty-state p {
  margin: 0;
  color: var(--text-color-secondary);
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
